import {showNotification} from '@elements/alert-notification';
import {assert} from "superstruct";
import axios from "axios";
import {getCurrencyRates, getLanguageConfig} from "./config-helper";


export function assertData(data, structConfig, errorTitle, errorText, type, closable = true) {
    try {
        assert(data, structConfig);
    } catch (error) {
        console.warn(error);
        throwAlertMessage(type, errorTitle, errorText + "<br>" + error, closable);
    }
}

export function throwAlertMessage(type, title, errorMessage, closable) {
    if (process.env.NODE_ENV === 'development') {
        showNotification({
            type: type,
            title: title,
            content: errorMessage,
            closable: closable
        });
    }
}

export function addParamsToUrl(url, searchParams) {
    url = new URL(url, location.origin);
    let searchParamsArray = Array.from(searchParams);
    searchParamsArray.forEach(([name]) => url.searchParams.delete(name));
    searchParamsArray.forEach(([name, value]) => url.searchParams.append(name, value));
    return url;
}

export async function fetcher(url) {
    // if url is an url object than use url.href instead
    // is needed, otherwise a javascript error will be thrown from the symfony toolbar
    if (typeof url === 'object') {
        url = url.href;
    }
    

    return await axios({
        method: 'GET',
        url: url,
        headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Content-Type': 'application/json',
        }}).then((res) => res.data)
}

export async function postFetcher(url) {
    // if url is an url object than use url.href instead
    // is needed, otherwise a javascript error will be thrown from the symfony toolbar
    if (typeof url === 'object') {
        url = url.href;
    }

    return await axios({
        method: 'POST',
        url: url,
        headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Content-Type': 'application/json'
        }}).then((res) => res.data)
}

export function isUndefined(value) {
    return typeof value === 'undefined' || value === null || value === "";
}

export function addDaysISO(isoDate, days) {
    const dayInMilliseconds = 24 * 60 * 60 * 1000;
    let date = UTCDateToLocalDate(ISOStringToDate(isoDate));
    let result = new Date(date.getTime() + days * dayInMilliseconds);
    return dateToISOString(result);
}

export function addDaysCeroTime(isoDate, days) {
    let date = ISOStringToDate(isoDate);
    let initialOffset = date.getTimezoneOffset();
    date.setDate(date.getDate() + (days-1));
    let newOffset = date.getTimezoneOffset();
    // Check if the timezone offset has changed indicating a DST transition
    if (newOffset !== initialOffset) {
        if (newOffset > initialOffset) {
            date.setHours(date.getHours() - 1);
        } else {
            date.setHours(date.getHours() + 1);
        }
    }
    date.setUTCHours(0, 0, 0, 0);
    return dateToISOString(date);
}

//for range
export function addDaysISOUTC(isoDate, days) {
    const dayInMilliseconds = 24 * 60 * 60 * 1000;
    let date = UTCDateToLocalDate(ISOStringToDate(isoDate));
    let result = localDateToUTCDate(new Date(date.getTime() + (days -1) * dayInMilliseconds));
    return dateToISOString(result);
}


export function ISOStringToDate(dateString) {
    if (dateString) {
        if (typeof dateString === "string") {
            dateString = dateString.replace(' ', 'T');
            if (dateString.includes('T')) {
                dateString += dateString.includes('Z') ? '' : 'Z';
            }
            return new Date(dateString);
        } else {
            console.warn('ISOStringToDate expects a string, but was given ', date);
        }
    }
}

export function dateToISOString(date, appendTime = true) {
    if (date) {
        if (date instanceof Date) {
            if (appendTime) {
                return date.toISOString().substring(0, 19);
            } else {
                return date.toISOString().substring(0, 10);
            }
        } else {
            console.warn('dateToISOString expects a date, but was given ', date);
        }
    }
}

export function areEqualArrays(array1, array2) {
    if (array1.length === array2.length) {
        return array1.every(element => {
            if (array2.includes(element)) {
                return true;
            }

            return false;
        });
    }

    return false;
}

export function convertInCurrency(price, currency) {
    let rates = getCurrencyRates();
    let rate = rates[currency];

    if (rate) {
        return formatPrice((price * rate).toFixed(2), currency);
    } else {
        return formatPrice(price);
    }
}

export function formatPrice(amount, currency){
    let lang = 'de-CH'; // e.g. en-US

    let style = new Intl.NumberFormat(lang, {
        style: 'currency',
        currency: currency, //CHF or EUR or USD
        currencyDisplay: 'code',
        minimumFractionDigits: 2
    });

    return style.format(amount);
}

export function localDateToUTCDate(date) {
    if (date){
        if (date instanceof Date){
            return new Date(date.getTime() - (date.getTimezoneOffset() * 60 * 1000));
        } else {
            console.warn('localDateToUTCDate expects a date, but was given ', date);
        }
    }
}


export function UTCDateToLocalDate(date) {
    if (date) {
        if (date instanceof Date){
            return new Date(date.getTime() + (date.getTimezoneOffset() * 60 * 1000));
        } else {
            console.warn('UTCDateToLocalDate expects a date, but was given ', date);
        }
    }
}

export function getLocaleDayNameOfISO(date) {
    let dateObject = UTCDateToLocalDate(ISOStringToDate(date));
    return dateObject.toLocaleDateString(getLanguageConfig(), {weekday: 'long'});
}

export function getLocaleMonthNameOfISO(date) {
    let dateObject = UTCDateToLocalDate(ISOStringToDate(date));
    return dateObject.toLocaleDateString(getLanguageConfig(), {month: 'long'});
}

export function getLocaleDayOfMonthOfISO(date) {
    let dateObject = UTCDateToLocalDate(ISOStringToDate(date));
    return dateObject.toLocaleDateString(getLanguageConfig(), {day: '2-digit'});
}

export function getLocaleYearOfISO(date) {
    let dateObject = UTCDateToLocalDate(ISOStringToDate(date));
    return dateObject.toLocaleDateString(getLanguageConfig(), {year: 'numeric'});
}

export function getLocaleDateString(date) {
    let dateObject = UTCDateToLocalDate(ISOStringToDate(date));
    return dateObject.toLocaleDateString(getLanguageConfig(), {year: 'numeric', month: 'long', day: 'numeric' });
}

export function getLocaleDateStringShort(date) {
    let dateObject = UTCDateToLocalDate(ISOStringToDate(date));
    return dateObject.toLocaleDateString(getLanguageConfig(), {year: 'numeric', month: 'short', day: 'numeric' });
}